import {
  AddressType,
  ComplianceCaseTypeEnum,
  ComplianceExternalObjectType,
  ComplianceObjectType,
  InstitutionAccountTypeEnum,
  TaxIdType,
} from "~/graphql/supergraphEnums";
import { EMPTY_PRODUCT_LINE_DISPLAY } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/EntityFields/productLine";
import { toCell } from "~/pages/Compliance/InvestorCaseNavigation/InvestorCaseWizard/ProfileStep/CompareTable/utils";
import {
  buildNewProfileMatchData,
  type CountryNameResolver,
} from "~/pages/Compliance/InvestorCaseNavigation/InvestorCaseWizard/ProfileStep/ProfileToInvestorView/utils";
import { AML_ENTITY_TYPE_ID_TO_NAME } from "~/pages/Compliance/utils/amlTypeIdUtil";
import { MAIN_SERVICE } from "~/pages/Compliance/utils/constants";
import { DATE_FORMATS, DEFAULT_DATE_FORMAT } from "~/utils/constants";
import { toStandardFormat } from "~/utils/datetime";
import { toDbId } from "~/utils/graphql-helpers";
import type {
  OnboardingCaseType,
  WizardCaseInfo,
  WizardProfileAddress,
  WizardProfileData,
} from "../types";
import type {
  Address,
  ComplianceInstitutionProfileInput,
  ExternalObjectInput,
} from "~/graphql/supergraphTypes";
import type {
  ProfileMatchData,
  ProfileMatchRow,
} from "~/pages/Compliance/InvestorCaseNavigation/InvestorCaseWizard/ProfileStep/ProfileToInvestorView/types";
import type { WizardProfileData as InvestorWizardProfileData } from "~/pages/Compliance/InvestorCaseNavigation/InvestorCaseWizard/types";

export const EMPTY_REVIEW_VALUE = "--";

export const resolveOnboardingCaseType = (
  caseType: OnboardingCaseType,
): ComplianceCaseTypeEnum =>
  caseType === "client"
    ? ComplianceCaseTypeEnum.ClientOnboarding
    : ComplianceCaseTypeEnum.EntityOnboarding;

export const buildExternalArena = (caseInfo: WizardCaseInfo): ExternalObjectInput | null => {
  if (caseInfo.clientId) {
    return {
      objectId: toDbId(caseInfo.clientId).toString(),
      objectTypeEnum: ComplianceExternalObjectType.Arena,
      service: MAIN_SERVICE,
      name: caseInfo.clientName ?? "",
    };
  }
  const unlinkedName = caseInfo.clientName?.trim();
  if (caseInfo.isUnlinked && unlinkedName) {
    return {
      objectId: "",
      objectTypeEnum: ComplianceExternalObjectType.Arena,
      service: MAIN_SERVICE,
      name: unlinkedName,
    };
  }
  return null;
};

export const buildExternalEntity = (
  caseInfo: WizardCaseInfo,
): ExternalObjectInput | undefined => {
  if (caseInfo.isUnlinked || !caseInfo.entityId) return undefined;
  return {
    objectId: toDbId(caseInfo.entityId).toString(),
    objectTypeEnum: ComplianceExternalObjectType.Account,
    service: MAIN_SERVICE,
    name: caseInfo.entityName ?? "",
  };
};

const buildAddresses = (address?: WizardProfileAddress): Address[] => [
  {
    addressType: AddressType.Unknown,
    street: address?.streetLine1,
    street2: address?.streetLine2,
    city: address?.city,
    state: address?.stateOrProvince,
    postalCode: address?.zipCode,
    country: address?.country || "US",
  },
];

const optionalStringField = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const optionalIntField = (value?: string | null): number | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) ? parsed : undefined;
};

export const buildInstitutionProfileInput = (
  profile: WizardProfileData,
): ComplianceInstitutionProfileInput => {
  const registrationNumber = optionalStringField(profile.registrationNumber);
  const natureOfBusiness = optionalStringField(profile.natureOfBusiness);
  const principalPlaceOfBusinessId = optionalIntField(profile.principalPlaceOfBusinessId);

  return {
    accountType: profile.accountType
      ? (profile.accountType as unknown as InstitutionAccountTypeEnum)
      : undefined,
    legalBusinessName: profile.legalBusinessName ?? "",
    dateOfFormation: profile.dateOfFormation,
    taxId: profile.taxId,
    taxIdType: profile.taxIdType ?? TaxIdType.Ein,
    addresses: buildAddresses(profile.address),
    amlEntityId: profile.amlEntityId,
    ...(registrationNumber !== undefined ? { registrationNumber } : {}),
    ...(natureOfBusiness !== undefined ? { natureOfBusiness } : {}),
    ...(principalPlaceOfBusinessId !== undefined ? { principalPlaceOfBusinessId } : {}),
  };
};

export const displayOrEmpty = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : EMPTY_REVIEW_VALUE;
};

export const formatAmlEntityTypeLabel = (amlEntityId?: number): string => {
  if (amlEntityId == null) return EMPTY_REVIEW_VALUE;
  return AML_ENTITY_TYPE_ID_TO_NAME[amlEntityId] ?? EMPTY_REVIEW_VALUE;
};

export const formatFormationDate = (isoDate?: string): string => {
  if (!isoDate) return EMPTY_REVIEW_VALUE;
  const formatted = toStandardFormat(isoDate, DATE_FORMATS[DEFAULT_DATE_FORMAT].short);
  return formatted || EMPTY_REVIEW_VALUE;
};

const presentOrNull = (value: string): string | null =>
  value === EMPTY_REVIEW_VALUE ? null : value;

const extraMatchRow = (label: string, value: string | null | undefined): ProfileMatchRow => ({
  label,
  complianceProfile: toCell(value),
  investmentAccount: {},
  status: "na",
});

export const buildOnboardingProfileMatchData = (args: {
  clientName?: string;
  profile: WizardProfileData;
  productLineFromMain?: string;
  resolveCountryName: CountryNameResolver;
  countryNameById?: Record<string, string>;
}): ProfileMatchData => {
  const { clientName, profile, productLineFromMain, resolveCountryName, countryNameById } =
    args;
  const investorProfile: InvestorWizardProfileData = {
    category: ComplianceObjectType.Institution,
    accountType: profile.accountType,
    legalBusinessName: profile.legalBusinessName,
    dateOfFormation: profile.dateOfFormation,
    amlEntityId: profile.amlEntityId,
    taxId: profile.taxId,
    taxIdType: profile.taxIdType,
    address: profile.address,
  };
  const matchData = buildNewProfileMatchData(
    investorProfile,
    null,
    resolveCountryName,
    clientName?.trim() || undefined,
  );
  const principalPlaceName = profile.principalPlaceOfBusinessId
    ? (countryNameById?.[profile.principalPlaceOfBusinessId] ??
      profile.principalPlaceOfBusinessId)
    : undefined;

  return {
    ...matchData,
    rows: [
      ...matchData.rows,
      extraMatchRow(
        "AML entity type",
        presentOrNull(formatAmlEntityTypeLabel(profile.amlEntityId)),
      ),
      extraMatchRow(
        "Registration number",
        presentOrNull(displayOrEmpty(profile.registrationNumber)),
      ),
      extraMatchRow(
        "Date of formation",
        presentOrNull(formatFormationDate(profile.dateOfFormation)),
      ),
      extraMatchRow(
        "Product line",
        productLineFromMain?.trim() ? productLineFromMain : EMPTY_PRODUCT_LINE_DISPLAY,
      ),
      extraMatchRow(
        "Nature of business",
        presentOrNull(displayOrEmpty(profile.natureOfBusiness)),
      ),
      extraMatchRow(
        "Principal place of business",
        presentOrNull(displayOrEmpty(principalPlaceName)),
      ),
    ],
  };
};

export const assignedToLabel = (assignToMyself: boolean): string =>
  assignToMyself ? "Myself" : "Unassigned";

export const missingProgramToastMessage = (
  caseType: OnboardingCaseType,
  caseInfo: Pick<WizardCaseInfo, "clientName" | "entityName">,
): string => {
  if (caseType === "client") {
    return `Case cannot be created because the client ${caseInfo.clientName ?? ""} does not have a compliance program set`;
  }
  return `Case cannot be created because the entity ${caseInfo.entityName ?? ""} does not have a compliance program set`;
};

export const isLinkedOnboardingSubject = (
  caseType: OnboardingCaseType,
  caseInfo: Pick<WizardCaseInfo, "isUnlinked" | "clientId" | "entityId">,
): boolean => {
  if (caseInfo.isUnlinked) return false;
  if (caseInfo.clientId) return true;
  return caseType === "entity" && Boolean(caseInfo.entityId);
};
